import { Link, useLocation } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useEffect, useRef, useState } from "react";
import { Wordmark } from "./Wordmark";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { BackButton } from "@/components/BackButton";
import { ThemeToggle } from "@/components/ThemeToggle";
import { FindMyPracticeButton } from "./FindMyPracticeButton";

export function Navbar() {
  const { user } = useAuth();
  const { t } = useI18n();
  const location = useLocation();
  const navRef = useRef<HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const showBack = location.pathname !== "/";

  useEffect(() => {
    const handleScroll = () => {
      if (navRef.current) {
        if (window.scrollY > 20) navRef.current.classList.add("scrolled");
        else navRef.current.classList.remove("scrolled");
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 bg-card border-b border-border transition-shadow duration-300 [&.scrolled]:shadow-[0_1px_3px_0_rgb(0_0_0/0.06)]"
      ref={navRef}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-6 px-6 md:grid md:grid-cols-[1fr_auto_1fr]">
        <div className="flex items-center gap-2 justify-self-start">
          {showBack && <BackButton className="hidden sm:inline-flex" />}
          <Wordmark className="text-2xl" />
        </div>

        <FindMyPracticeButton className="hidden md:flex w-[28rem] justify-self-center" />

        <div className="flex items-center gap-6 justify-self-end">
          <Link
            to="/list-your-practice"
            className="hidden sm:inline-flex text-xs font-semibold tracking-[0.14em] uppercase text-foreground/80 hover:text-foreground transition-colors"
          >
            {t.nav.list}
          </Link>




          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                aria-label="Browse menu"
                className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.14em] uppercase text-foreground/80 hover:text-foreground transition-colors"
              >
                <Menu className="h-5 w-5" />
                <span>{t.nav.browse}</span>
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85%] sm:w-[360px]">
              <SheetHeader>
                <SheetTitle>
                  <Wordmark className="text-lg" />
                </SheetTitle>
              </SheetHeader>
              <div className="mt-8 flex flex-col gap-1">

                <Link
                  to="/browse"
                  onClick={() => setOpen(false)}
                  className="px-3 py-3 text-base text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
                >
                  {t.nav.browse}
                </Link>

                <Link
                  to="/list-your-practice"
                  onClick={() => setOpen(false)}
                  className="px-3 py-3 text-base text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors sm:hidden"
                >
                  {t.nav.list}
                </Link>
              </div>
              <div className="mt-6 pt-6 border-t border-border flex flex-col gap-2">
                {user ? (
                  <Button asChild onClick={() => setOpen(false)}>
                    <Link to="/dashboard">{t.nav.dashboard}</Link>
                  </Button>
                ) : (
                  <>
                    <Button variant="outline" asChild onClick={() => setOpen(false)}>
                      <Link to="/login">{t.nav.login}</Link>
                    </Button>
                    <Button asChild onClick={() => setOpen(false)}>
                      <Link to="/login">{t.nav.signup}</Link>
                    </Button>
                  </>
                )}
              </div>
              <div className="mt-6 pt-6 border-t border-border flex items-center justify-between">
                <LanguageSwitcher />
                <ThemeToggle />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      {/* Mobile-only FindMyPractice row */}
      <div className="md:hidden border-t border-border px-4 py-2">
        <FindMyPracticeButton className="w-full" />
      </div>
    </nav>
  );
}
